import { Drawer } from "@material-ui/core";
import { Trans } from "react-i18next";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useUi } from "application/contexts/ui/UIContext";
import { Keyboard } from "components/keyboard/Keyboard";
import { PassKeyboard } from "components/passKeyboard/PassKeyboard";
import { TransactionType } from "domains/order/agreggates/payment/transactionType";
import { DoCashlessCosumeUseCase } from "modules/order/application/useCases/DoCashlessCosumeUseCase";
import { ISyncNominalOrderResult } from "modules/order/application/useCases/ISyncNominalOrderResult";
import { ICustomer } from "modules/order/domain/models/ICustomer";
import { IOrder } from "modules/order/domain/models/IOrder";
import { ICardCashless } from "modules/payment/domain/models/ICashless";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createDefferedPromise } from "utils/DefferedPromise";
import styles from "./NominalCashless.module.scss";
import { ReactNativeFacial } from "Infrastructure/services/reactNativeServices/ReactNativeFacial";
import { useCardReaderStore } from "modules/cardReader/infra/store/CardReaderStore";
import { useShallow } from "zustand/react/shallow";
import { CardDataSource, ICardReaderStore } from "modules/cardReader/domain/interfaces/IUseCardReaderStore";
import { FacialErrorToast } from "components/facialErrorToast/FacialErrorToast";
import { SimilarFaceSelection } from "components/similarFaceSelection";
import type { SimilarFace } from "modules/reactNativeBridge/FacialReactNativeBridge";

const cashlessRepository = CashlessRepository();

export interface INominalCashlessHandler {
  open(
    order: IOrder,
    cardData: ICardCashless
  ): Promise<ISyncNominalOrderResult>;
}

interface ICashlessProps { }

const UseNominalCashless: React.ForwardRefRenderFunction<
  INominalCashlessHandler,
  ICashlessProps
> = (_, ref) => {
  const deffered = useRef(createDefferedPromise<ISyncNominalOrderResult>());
  const { toast, showLoading, hideLoading } = useUi();

  const [visible, setVisible] = useState(false);
  const [order, setOrder] = useState<IOrder>({} as IOrder);
  const [password, setPassword] = useState("");
  const [cardData, setCardData] = useState<ICardCashless>();
  const { kiosk } = useKiosk();
  const [cardDataSource] = useCardReaderStore(useShallow((state: ICardReaderStore) => [
    state.cardDataSource,
  ]));
  const [showFacialError, setShowFacialError] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState<{order: IOrder, cardData: ICardCashless} | null>(null);
  const [similarFacesToSelect, setSimilarFacesToSelect] = useState<SimilarFace[] | null>(null);
  const [pendingFacialOrderData, setPendingFacialOrderData] = useState<{order: IOrder, cardData: ICardCashless} | null>(null);

  const enableFacialRecognition = Boolean(kiosk?.shouldUseFacialRecognition);
  //const doubleConfirmation = true ////TODO: Implementar configuração no kiosk

  useImperativeHandle(ref, () => ({ open }));

  const onKeyPress = useCallback(
    (key: string) => {
      if (password.length < 4) {
        setPassword(password + key);
      }
    },
    [password]
  );
  const onClearPress = useCallback(() => {
    setPassword("");
  }, []);

  const onRemovePress = useCallback(() => {
    setPassword((prev) => prev.substring(0, prev.length - 1));
  }, []);

  useEffect(() => {
    if (!visible) {
      setPassword("");
    }
  }, [visible]);

  const onConfirmPassword = useCallback(
    async (validated?: boolean, _order?: IOrder, _cardData?: ICardCashless) => {
      const currentCardData = _cardData || cardData;
      const currentOrder = _order || order;
      if (currentCardData?.password === password || validated) {
        if (!kiosk?.localId) {
          toast("Falha ao efetuar cobrança no cartão", "error");
          setVisible(false);
          deffered.current.rejecter("Falha no local corrente");
        } else {
          const customer: ICustomer = {
            ...currentOrder.customer,
            document: currentCardData?.holderDocument,
            name: currentCardData?.holderName,
            associationId: currentCardData?.associationId,
            tag: currentCardData?.number,
          };

          const orderWithCustomer: IOrder = {
            ...currentOrder,
            customer,
          };

          showLoading();

          const syncResult = await DoCashlessCosumeUseCase(
            cashlessRepository,
            orderWithCustomer,
            TransactionType.cashless,
            kiosk
          );

          hideLoading();

          if (!syncResult.success) {
            setVisible(false);
            deffered.current.rejecter(
              syncResult?.message ?? "Falha ao sincronizar consumo"
            );
          }

          setVisible(false);
          deffered.current.resolver({
            ...syncResult,
            order: orderWithCustomer,
          });
        }
      } else {
        toast("Digitos não correspondem", "error");
        setPassword("");
      }
    },
    [cardData, hideLoading, kiosk, order, password, showLoading, toast]
  );

  const onCancel = useCallback(() => {
    setVisible(false);
    setPassword("");
    deffered.current?.rejecter(
      new Error("Fechado pelo usuário ou tempo esgotado")
    );
  }, []);


  const handleFacialRecognition = useCallback(async (_cardData: ICardCashless, _order: IOrder) => {
    if (kiosk) {
      if (enableFacialRecognition) {
        try {
          showLoading();
          const response = await ReactNativeFacial().captureAndIdentify({
            personGroupId: kiosk?.localId ?? "",
            autoCapture: true,
          });
          const newCardData = response.userData;
          console.log(`cardData:`, _cardData, newCardData);

          if (!response.success) {
            hideLoading();
            setPendingOrderData({ order: _order, cardData: _cardData });
            setShowFacialError(true);
            return;
          }

          if (response.similarFaces && response.similarFaces.length >= 2) {
            hideLoading();
            setSimilarFacesToSelect(response.similarFaces);
            setPendingFacialOrderData({ order: _order, cardData: _cardData });
            return;
          }

          const resolvedUserData = newCardData ?? (response.similarFaces?.[0]
            ? JSON.parse(response.similarFaces[0].userData)
            : null);

          if (!response.isIdentified || !resolvedUserData) {
            hideLoading();
            setPendingOrderData({ order: _order, cardData: _cardData });
            setShowFacialError(true);
            return;
          }

          if (_cardData?.nickname === resolvedUserData?.nickname) {
            onConfirmPassword(true, _order, _cardData);
            return deffered.current.promise;
          } else {
            hideLoading();
            setPendingOrderData({ order: _order, cardData: _cardData });
            setShowFacialError(true);
          }
        } catch (error) {
          hideLoading();
          setPendingOrderData({ order: _order, cardData: _cardData });
          setShowFacialError(true);
          console.error("Erro no reconhecimento facial", error);
        }
      }
    }
  }, [kiosk, enableFacialRecognition, showLoading, hideLoading, onConfirmPassword]);

  const handleSimilarFaceSelect = useCallback((face: SimilarFace) => {
    const pending = pendingFacialOrderData;
    setSimilarFacesToSelect(null);
    setPendingFacialOrderData(null);
    if (!pending) return;
    try {
      const userData = JSON.parse(face.userData) as { nickname?: string };
      if (pending.cardData?.nickname === userData?.nickname) {
        onConfirmPassword(true, pending.order, pending.cardData);
      } else {
        setPendingOrderData(pending);
        setShowFacialError(true);
      }
    } catch {
      setPendingOrderData(pending);
      setShowFacialError(true);
    }
  }, [pendingFacialOrderData, onConfirmPassword]);

  const handleSimilarFaceCancel = useCallback(() => {
    setSimilarFacesToSelect(null);
    setPendingFacialOrderData(null);
    setVisible(true);
  }, []);



  const open = useCallback((_order: IOrder, _cardData: ICardCashless) => {
    setOrder(_order);
    setCardData(_cardData);
    deffered.current = createDefferedPromise<ISyncNominalOrderResult>();
    if (enableFacialRecognition && cardDataSource === CardDataSource.FacialRecognition) {
      if (!kiosk?.disableDoubleFacialValidation) {
        handleFacialRecognition(_cardData, _order);
      } else {
        onConfirmPassword(true, _order, _cardData);
      }
    } else {
      setVisible(true);
    }
    return deffered.current.promise;
  }, [enableFacialRecognition, cardDataSource, kiosk, handleFacialRecognition, onConfirmPassword]);

  const handleRetryFacialRecognition = () => {
    setShowFacialError(false);
    if (pendingOrderData) {
      handleFacialRecognition(pendingOrderData.cardData, pendingOrderData.order);
    }
  };

  const handleBackFromFacialError = () => {
    setShowFacialError(false);
    setPendingOrderData(null);
    setVisible(true);
  };

  return (
    <>
      <Drawer anchor="bottom" open={visible} onClose={onCancel}>
        <div className={styles.drawerPaymentCashless}>
          <h2>
            <Trans i18nKey="cashless.nominal.password_prompt">
              Digite sua <b>senha </b>
            </Trans>
          </h2>
          {!kiosk?.useStrongPassword && (
            <div id={styles.passwordPaymentCashless}>
              {password.replace(/[0-9]/g, "*")}
            </div>
          )}
          {kiosk?.useStrongPassword ? (
            <PassKeyboard
              onValidate={onConfirmPassword}
              password={cardData?.password ?? "xxx"}
            />
          ) : (
            <Keyboard
              onKeyPress={onKeyPress}
              onPressClear={onClearPress}
              onPressBack={onRemovePress}
              onPressEnter={onConfirmPassword}
              type="numeric"
            />
          )}
        </div>
      </Drawer>
      
      <SimilarFaceSelection
        isOpen={similarFacesToSelect != null && similarFacesToSelect.length >= 2}
        similarFaces={similarFacesToSelect ?? []}
        onSelect={handleSimilarFaceSelect}
        onCancel={handleSimilarFaceCancel}
        title="Mais de uma pessoa similar. Selecione quem está realizando o pedido:"
      />
      <FacialErrorToast
        isOpen={showFacialError}
        onRetry={handleRetryFacialRecognition}
        onBack={handleBackFromFacialError}
        onClose={handleBackFromFacialError}
        message="O rosto não corresponde com o usuário que está realizando o pedido"
      />
    </>
  );
};

export default forwardRef(UseNominalCashless);
